/** What became of an export (2026-09-11).
 *
 * In a browser a download needs no notice at all: the browser writes it to the download folder
 * and lists it, and an extra line of ours would be noise. In the Teacher Computer app there is
 * no such list, so the app has to say where the file went and offer to show it. One hook and
 * one line, so every screen that exports says the same thing the same way.
 *
 * The hook holds only the last result; it is a receipt, not a history.
 */
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { capabilities, type SaveResult } from "../platform/capabilities";
import { Button } from "./Button";

export function useFileSave() {
  const [result, setResult] = useState<SaveResult | null>(null);
  const save = useCallback(async (filename: string, blob: Blob): Promise<SaveResult> => {
    const r = await capabilities.fileSave.save(filename, blob);
    setResult(r);
    return r;
  }, []);
  /** For a file the caller fetched through `download()`, which saves it itself. */
  const record = useCallback((r: SaveResult | null) => setResult(r), []);
  return { save, record, result };
}

export function SaveNotice({ result }: { result: SaveResult | null }) {
  const { t } = useTranslation();
  const reveal = capabilities.fileSave.reveal;
  // The browser's own download list is the receipt: adding one here would be noise.
  if (!result || result.kind === "browser") return null;
  if (result.kind === "failed") {
    return (
      <p role="alert" className="text-sm" style={{ color: "var(--danger)" }}>
        {t("save.failed")}
      </p>
    );
  }
  return (
    <p role="status" className="flex flex-wrap items-center gap-x-2 text-sm">
      {/* The path is the point: it is the only way the teacher finds the file again, so it is
          shown whole and allowed to break -- a home directory and a long course title together
          are wider than the card. */}
      <span className="min-w-0 break-all">{t("save.saved", { path: result.path })}</span>
      {reveal && (
        // The compact step, not the touch floor: `reveal` is non-null only in the desktop
        // shell, which is a mouse on a teacher's computer, never a student or live surface.
        <Button variant="tertiary" size="sm" onClick={() => void reveal(result.path)}>
          {t("save.reveal")}
        </Button>
      )}
    </p>
  );
}
